import {
  Admin as RaAdmin,
  CustomRoutes,
  Resource,
  type TranslationMessages,
  resolveBrowserLocale,
  combineDataProviders
} from 'react-admin'
import { useMediaQuery } from '@mui/material'
import polyglotI18nProvider from 'ra-i18n-polyglot'
import { Route, useParams } from 'react-router-dom'
import log from 'loglevel'
import React from 'react'
import { Login } from 'ra-auth-cognito'
import { ja } from './translations-ja'
import { DataProvider } from 'components/DataProvider'
import { CognitoDataProvider } from 'components/CognitoDataProvider'
import { Dashboard } from 'components/Dashboard'
import { Layout } from 'components/Layout'
import { cognitoAuthProvider } from 'components/Auth'
import { adminTheme, desktopTtheme, otherTheme } from 'components/Theme'
import { includes, isNil } from 'lodash/fp'
import { getPartnerId } from 'components/DataProvider/functions'
import { customTranslates } from './customTranslate'
import { AdminDashboard } from 'components/AdminDashboard'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const translations: Record<string, TranslationMessages> = { ja }

const defaultI18nProvider = polyglotI18nProvider(
  (locale) => translations[locale] as TranslationMessages,
  resolveBrowserLocale(),
  [{ locale: 'ja', name: '日本語' }],
  { allowMissing: true }
)
// const partnerId = 'f698dea9-d49f-43dc-b26d-0096b5bb8c8e'

export const Admin: React.FC<{ admin?: boolean }> = ({ admin }) => {
  const isDesktop = useMediaQuery('(min-width: 900px)')
  const appCode = localStorage.getItem('appCode')
  const [partnerId, setPartnerId] = React.useState<string | undefined>(
    undefined
  )
  const { partner: partnerParam } = useParams()
  const code = partnerParam ?? appCode
  React.useEffect(() => {
    if (!isNil(partnerId)) return
    if (isNil(code)) return
    getPartnerId(code)
      .then((id: string | undefined) => {
        if (isNil(id)) return
        setPartnerId(id)
        localStorage.setItem('appCode', code)
      })
      .catch(log.error)
  }, [partnerParam, code])
  if (isNil(partnerId)) return
  const authProvider = cognitoAuthProvider(partnerId)
  if (isNil(authProvider)) return null
  const dataProvider = combineDataProviders((resource: string) => {
    const restDataProvider = DataProvider(partnerId)
    if (includes(resource)(['accounts', 'mappings'])) {
      return CognitoDataProvider(partnerId)
    }
    return restDataProvider
  })
  if (
    (isNil(admin) || !admin) &&
    window.location.pathname !== `/menu/${code as string}` &&
    window.location.pathname !== '/login'
  ) {
    // @ts-expect-error ts(2322)
    window.location = `menu/${code as string}`
  }
  const i18nProvider = {
    ...defaultI18nProvider,
    translate: (key: string, options: Record<string, any>): string => {
      const { translate } = defaultI18nProvider
      if (!isNil(partnerId)) {
        return customTranslates(partnerId, key, options, translate)
      }
      return translate(key, options)
    }
  }
  return isNil(admin) || !admin ? (
    <RaAdmin
      dataProvider={dataProvider}
      i18nProvider={i18nProvider}
      authProvider={authProvider}
      loginPage={Login}
      requireAuth
      layout={Layout}
      lightTheme={isDesktop ? desktopTtheme : otherTheme}>
      <CustomRoutes>
        <Route path="/" element={<Dashboard partner={partnerId} />} />
      </CustomRoutes>
      <Resource name="organizations" />
      <Resource name="summarys" />
      <Resource name="accounts" />
    </RaAdmin>
  ) : (
    <RaAdmin
      dataProvider={dataProvider}
      i18nProvider={i18nProvider}
      authProvider={authProvider}
      loginPage={Login}
      requireAuth
      layout={Layout}
      theme={adminTheme}>
      <CustomRoutes>
        <Route path="/" element={<AdminDashboard partner={partnerId} />} />
      </CustomRoutes>
    </RaAdmin>
  )
}
